import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Navbar from "@/components/navbar";
import { useAuth } from "@/hooks/useAuthState";
import { 
  Trophy, 
  Calendar, 
  Gift,
  Star,
  Crown,
  Award,
  RefreshCw,
  Sparkles,
  MessageSquare,
  ChevronRight,
  Target
} from "lucide-react";
import { useLocation } from "wouter";
import { queryClient } from "@/lib/queryClient";

// Public winner interface based on API response
interface PublicWinner {
  id: number;
  firstName: string;
  lastName: string;
  prizeTitle: string;
  prizeValue: string;
  winDate: string;
  month: string;
  year: number;
  prizeImageUrl?: string;
  mediaUrl?: string;
  testimony?: string;
  mediaType?: 'photo' | 'video' | 'testimony';
  prizeClaimed: boolean;
  claimedDate?: string | null;
  claimedDateFormatted?: string | null;
}

interface PublicWinnersResponse {
  winners: PublicWinner[];
  totalWinners: number;
  totalPrizeValue: string;
  timestamp: string;
  cached: boolean;
}

// Challenge winner interface
interface ChallengeWinner {
  id: number;
  position: number;
  userId: number;
  challengeId: number;
  challengeName: string;
  challengeStatus: string;
  displayName: string;
  progressCount: number;
  referralCount: number;
  winningType: string;
  wonAt: string;
  prize: string | null;
}

interface ChallengeWinnersResponse {
  winners: ChallengeWinner[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Mobile winner card component
const MobileWinnerCard = ({ winner, onSelect }: { winner: PublicWinner; onSelect: (winner: PublicWinner) => void }) => (
  <Card 
    className="hover:shadow-md transition-shadow duration-200 cursor-pointer active:scale-95"
    onClick={() => onSelect(winner)}
    data-testid={`card-winner-${winner.id}`}
  >
    <CardContent className="p-0">
      {/* Header with gradient background */}
      <div className={`bg-gradient-to-r p-4 ${
        winner.prizeClaimed 
          ? 'from-green-600 to-green-700' 
          : 'from-blue-600 to-blue-700'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
              {winner.prizeClaimed ? (
                <Crown className="w-5 h-5 text-yellow-300" />
              ) : (
                <Trophy className="w-5 h-5 text-yellow-300" />
              )}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <p className="text-white font-bold text-base" data-testid={`text-winner-name-${winner.id}`}>
                  {winner.firstName} {winner.lastName}
                </p>
                {winner.prizeClaimed && (
                  <div className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold">
                    CLAIMED
                  </div>
                )}
              </div>
              <p className="text-white/80 text-sm" data-testid={`text-win-date-${winner.id}`}>
                {winner.prizeClaimed && winner.claimedDateFormatted 
                  ? `Claimed ${winner.claimedDateFormatted}`
                  : `Won ${winner.winDate}`
                }
              </p>
            </div>
          </div>
          <div className="flex">
            {winner.prizeClaimed ? (
              <div className="flex flex-col items-center">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <span className="text-white/80 text-xs font-semibold">CLAIMED</span>
              </div>
            ) : (
              [...Array(5)].map((_, i) => (
                <Star key={i} className="w-3 h-3 text-yellow-300 fill-current" />
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* Prize information */}
      <div className="p-4">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gray-200 rounded-xl shadow-md flex items-center justify-center flex-shrink-0">
            {winner.prizeImageUrl ? (
              <img 
                src={winner.prizeImageUrl} 
                alt={winner.prizeTitle}
                className="w-full h-full object-cover rounded-xl"
              />
            ) : (
              <Gift className="w-8 h-8 text-gray-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-800 text-sm mb-2 line-clamp-2" data-testid={`text-prize-title-${winner.id}`}>
              {winner.prizeTitle}
            </h3>
            <div className="bg-gradient-to-r from-blue-100 to-blue-200 inline-flex px-3 py-1 rounded-full">
              <span className="text-blue-700 font-bold text-sm" data-testid={`text-prize-value-${winner.id}`}>
                {winner.prizeValue}
              </span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-blue-500 flex-shrink-0" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Challenge Winner Card Component
const ChallengeWinnerCard = ({ winner }: { winner: ChallengeWinner }) => {
  const wonDate = new Date(winner.wonAt).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <Card 
      className="hover:shadow-md transition-shadow duration-200"
      data-testid={`card-challenge-winner-${winner.id}`}
    >
      <CardContent className="p-0">
        <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-purple-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                <Crown className="w-5 h-5 text-yellow-300" />
              </div>
              <div>
                <p className="text-white font-bold text-base" data-testid={`text-challenge-winner-name-${winner.id}`}>
                  {winner.displayName}
                </p>
                <p className="text-white/80 text-sm">
                  Won {wonDate}
                </p>
              </div>
            </div>
            <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
              1st PLACE
            </div>
          </div>
        </div>
        
        <div className="p-4">
          <div className="space-y-3">
            <div>
              <h3 className="font-bold text-gray-800 text-sm mb-1" data-testid={`text-challenge-name-${winner.id}`}>
                {winner.challengeName}
              </h3>
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-gray-600">
                  {winner.referralCount} verified referrals
                </span>
              </div>
            </div>
            {winner.prize && (
              <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-3 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Gift className="w-4 h-4 text-purple-600" />
                  <span className="text-purple-700 font-bold text-sm" data-testid={`text-challenge-prize-${winner.id}`}>
                    {winner.prize}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Winner detail modal for mobile
const WinnerDetailModal = ({ winner, onClose }: { winner: PublicWinner | null; onClose: () => void }) => {
  if (!winner) return null;

  return (
    <Dialog open={!!winner} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Trophy className="w-5 h-5 text-yellow-600" />
            <span>Winner Details</span>
          </DialogTitle>
          <DialogDescription>
            Celebration of our latest winner
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Winner Info Header */}
          <div className={`bg-gradient-to-r p-4 rounded-2xl ${
            winner.prizeClaimed 
              ? 'from-green-600 to-green-700' 
              : 'from-blue-600 to-blue-700'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                {winner.prizeClaimed ? (
                  <Crown className="w-6 h-6 text-green-600" />
                ) : (
                  <Trophy className="w-6 h-6 text-blue-600" />
                )}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-white font-bold text-lg" data-testid={`text-modal-winner-name-${winner.id}`}>
                    {winner.firstName} {winner.lastName}
                  </h3>
                  {winner.prizeClaimed && (
                    <Award className="w-4 h-4 text-yellow-300" />
                  )}
                </div>
                <p className="text-white/80 text-sm">
                  {winner.prizeClaimed ? (
                    `Prize claimed on ${winner.claimedDateFormatted || winner.winDate}`
                  ) : (
                    `Won on ${winner.winDate}`
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Prize Details */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-20 h-20 bg-gray-200 rounded-xl flex items-center justify-center">
                  {winner.prizeImageUrl ? (
                    <img 
                      src={winner.prizeImageUrl} 
                      alt={winner.prizeTitle}
                      className="w-full h-full object-cover rounded-xl"
                    />
                  ) : (
                    <Gift className="w-10 h-10 text-gray-400" />
                  )}
                </div>
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                  WON!
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Gift className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-blue-600 font-semibold">PRIZE WON</span>
                </div>
                <h4 className="font-bold text-gray-800 mb-2" data-testid={`text-modal-prize-title-${winner.id}`}>
                  {winner.prizeTitle}
                </h4>
                <div className="bg-white px-3 py-2 rounded-full inline-flex">
                  <span className="text-blue-700 font-bold text-sm" data-testid={`text-modal-prize-value-${winner.id}`}>
                    Value: {winner.prizeValue}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Media Display */}
          {winner.mediaUrl && (
            <div className="aspect-video bg-gray-200 rounded-2xl overflow-hidden">
              {winner.mediaType === 'video' ? (
                <video controls className="w-full h-full" data-testid={`video-modal-${winner.id}`}>
                  <source src={winner.mediaUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={winner.mediaUrl} 
                  alt="Winner celebration"
                  className="w-full h-full object-cover"
                  data-testid={`img-modal-${winner.id}`}
                />
              )}
            </div>
          )}

          {/* Testimony */}
          {winner.testimony && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-700 leading-relaxed italic" data-testid={`text-modal-testimony-${winner.id}`}>
                    "{winner.testimony}"
                  </p>
                  <p className="mt-3 text-sm font-semibold text-blue-600">
                    — {winner.firstName} {winner.lastName}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function MobileWinnersSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i}>
          <CardContent className="p-0">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
              <div className="flex items-center space-x-3">
                <Skeleton className="h-10 w-10 rounded-full bg-white/20" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32 bg-white/20" />
                  <Skeleton className="h-3 w-24 bg-white/20" />
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center space-x-4">
                <Skeleton className="h-16 w-16 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function MobileWinners() {
  const { user } = useAuth();
  const [selectedWinner, setSelectedWinner] = useState<PublicWinner | null>(null);
  const [, setLocation] = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Manual refresh function
  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['/api/public/winners'] });
      console.log('[MOBILE WINNERS] Manual refresh triggered');
    } catch (error) {
      console.error('[MOBILE WINNERS] Manual refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Fetch winners data
  const { data: winnersData, isLoading, error } = useQuery<PublicWinnersResponse>({
    queryKey: ['/api/public/winners'],
    queryFn: async () => {
      const response = await fetch('/api/public/winners');
      if (!response.ok) {
        throw new Error('Failed to fetch winners');
      }
      const data = await response.json();
      console.log(`[MOBILE WINNERS] Data fetched - Cached: ${data.cached}, Winners: ${data.totalWinners}`);
      return data;
    },
    refetchInterval: 120000, // Refresh every 2 minutes
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch challenge winners - only position 1 from ended challenges
  const { data: challengeWinnersData, isLoading: challengeWinnersLoading } = useQuery<ChallengeWinnersResponse>({
    queryKey: ['/api/v1/challenges/winners/all'],
    queryFn: async () => {
      const response = await fetch('/api/v1/challenges/winners/all?page=1&pageSize=100');
      if (!response.ok) {
        throw new Error('Failed to fetch challenge winners');
      }
      return response.json();
    },
    staleTime: 60000, // 1 minute
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
        {/* Loading Header */}
        <div className="text-white" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
          <div className="px-4 pt-12 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3 flex-1">
                <div>
                  <h1 className="text-2xl font-bold">Winners</h1>
                  <p className="text-blue-100 text-sm">Loading our amazing winners...</p>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <Trophy className="h-6 w-6 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-4 py-6">
          <MobileWinnersSkeleton />
        </div>
        
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
        {/* Error Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white">
          <div className="px-4 pt-12 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3 flex-1">
                <div>
                  <h1 className="text-2xl font-bold">Winners</h1>
                  <p className="text-red-100 text-sm">Unable to load winners</p>
                </div>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <Trophy className="h-6 w-6" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-4 py-16 text-center">
          <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Unable to Load Winners</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Please check your connection and try again</p>
          <Button onClick={handleManualRefresh} data-testid="button-retry">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
        
      </div>
    );
  }

  const winners: PublicWinner[] = winnersData?.winners || [];
  const totalPrizeValue = winnersData?.totalPrizeValue || 'R0';

  // Group winners by month for organization
  const winnersByMonth = winners.reduce((acc, winner) => {
    const monthKey = `${winner.month} ${winner.year}`;
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(winner);
    return acc;
  }, {} as Record<string, PublicWinner[]>);

  const monthKeys = Object.keys(winnersByMonth).sort((a, b) => {
    const [monthA, yearA] = a.split(' ');
    const [monthB, yearB] = b.split(' ');
    const dateA = new Date(`${monthA} 1, ${yearA}`);
    const dateB = new Date(`${monthB} 1, ${yearB}`);
    return dateB.getTime() - dateA.getTime(); // Most recent first
  });

  // Filter challenge winners to show only position 1 from ended challenges
  const firstPlaceChallengeWinners = challengeWinnersData?.winners.filter(
    winner => winner.position === 1 && winner.challengeStatus === 'ended'
  ) || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Show Navbar for logged-out users */}
      {!user && <Navbar />}
      {/* Mobile Header */}
      <div className="text-white" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="px-4 pt-12 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3 flex-1">
              <div>
                <h1 className="text-2xl font-bold">Winners</h1>
                <p className="text-blue-100 text-sm">
                  Real people, real prizes!
                </p>
              </div>
            </div>
            <div className="bg-white/20 rounded-full p-3">
              <Trophy className="h-6 w-6" />
            </div>
          </div>
          
          {/* Stats */}
          <div className="bg-white/20 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-xl font-bold">{winners.length}</div>
                <div className="text-xs text-blue-100">Total Winners</div>
              </div>
              <div>
                <div className="text-xl font-bold">{totalPrizeValue}</div>
                <div className="text-xs text-blue-100">Total Value</div>
              </div>
            </div>
          </div>

          {/* Data freshness indicator */}
          <div className="mt-3 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                winnersData?.cached === false ? 'bg-green-400 animate-pulse' : 'bg-white/60'
              }`}></div>
              <span className="text-white/90 text-xs">
                {winnersData?.cached === false ? 'Live Data' : 'Cached Data'}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="text-white hover:bg-white/20 px-2 py-1 h-auto"
              data-testid="button-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Content with Tabs */}
      <div className="px-4 py-6">
        <Tabs defaultValue="competitions" className="w-full">
          <TabsList className="w-full mb-6">
            <TabsTrigger value="competitions" className="flex-1" data-testid="tab-competitions">
              <Trophy className="w-4 h-4 mr-2" />
              Competitions
            </TabsTrigger>
            <TabsTrigger value="challenges" className="flex-1" data-testid="tab-challenges">
              <Target className="w-4 h-4 mr-2" />
              Challenge Winners
            </TabsTrigger>
          </TabsList>

          <TabsContent value="competitions">
            {winners.length === 0 ? (
              <div className="text-center py-16">
                <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No Winners Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Be the first to win amazing prizes!
                </p>
                <Button 
                  onClick={() => setLocation('/m/competitions')}
                  data-testid="button-view-competitions"
                >
                  View Competitions
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {monthKeys.map((monthKey) => (
                  <div key={monthKey}>
                    <div className="flex items-center space-x-2 mb-4">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                        {monthKey}
                      </h2>
                      <Badge variant="outline" className="text-xs">
                        {winnersByMonth[monthKey].length} winner{winnersByMonth[monthKey].length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="space-y-4">
                      {winnersByMonth[monthKey].map((winner) => (
                        <MobileWinnerCard 
                          key={winner.id} 
                          winner={winner}
                          onSelect={setSelectedWinner}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="challenges">
            {challengeWinnersLoading ? (
              <div className="text-center py-12">
                <div className="flex justify-center">
                  <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
                </div>
                <p className="mt-4 text-gray-600">Loading challenge winners...</p>
              </div>
            ) : firstPlaceChallengeWinners.length === 0 ? (
              <div className="text-center py-16">
                <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  No Challenge Winners Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Challenge winners will appear here when challenges are completed
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6 text-center">
                  <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-100 to-purple-200 px-4 py-2 rounded-full">
                    <Crown className="w-4 h-4 text-purple-600" />
                    <span className="text-purple-700 font-semibold text-sm">First to Reach Target</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {firstPlaceChallengeWinners.map((winner) => (
                    <ChallengeWinnerCard key={winner.id} winner={winner} />
                  ))}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Winner Detail Modal */}
      <WinnerDetailModal 
        winner={selectedWinner}
        onClose={() => setSelectedWinner(null)}
      />

      {/* Bottom Tab Navigation */}
    </div>
  );
}